import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {BaseJsonService} from '@shared/services/base.service';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {ObjectResponse} from '@shared/models';
import {
	CompositeFieldComponentPart,
	DynamicConfigModel,
	DynamicFieldBaseModel,
	DynamicFieldCompositeModel,
	DynamicFieldModel
} from '../../models/config';
import {FieldTypes} from '../../models/enums/field.types.enum';
import {SelectItem} from 'src/models/general/select.item.model';
import { HttpErrorResponse } from '@angular/common/http';
import {LabelUUIDs} from 'src/models/general/uuid.const';

interface DynamicConfigUpdateModel {
	// Key = Label-Uuid
	[key: string]: DynamicFieldModel[];
}

@Injectable({
	providedIn: 'root'
})
export class DynamicConfigService extends BaseJsonService {
	private fieldNameIdMap: Map<string, string> = new Map<string, string>();

	private fieldTypeDisplayNames: Map<FieldTypes, string> = new Map([
		[FieldTypes.custom, 'Zusammengesetztes Feld'],
		[FieldTypes.freeText, 'Freitext'],
		[FieldTypes.select, 'Auswahlliste'],
		[FieldTypes.upload, 'Upload'],
		[FieldTypes.multiselect, 'Mehrfachauswahl'],
		[FieldTypes.reference, 'Referenz (Sub-Label)']
	]);

	private dynamicConfig$: Observable<DynamicConfigModel> | undefined;
	private dynamicConfigRaw$: Observable<DynamicConfigModel> | undefined;

	getFieldTypesSelectItems(): Observable<SelectItem[]> {
		return of(this.mapToSelectList(FieldTypes, this.fieldTypeDisplayNames));
	}

	getDisplayNameFor(fieldType: FieldTypes): string {
		return this.fieldTypeDisplayNames.get(fieldType) || fieldType.toString();
	}

	getDynamicFieldsForLabel(labelUuid: string): Observable<DynamicFieldModel[]> {
		return this.loadDynamicConfig().pipe(
			map((conf: DynamicConfigModel) => {
				return conf.fieldList.filter((field: DynamicFieldModel) => {
					return field.label && field.label === labelUuid;
				});
			})
		);
	}

	/**
	 * Return all dynamic-fields as select-item
	 * @param filterFunc optional function to filter dynamic-fields before returning them as select-items
	 */
	getDynamicFieldsAsSelectItem(filterFunc?: (field: DynamicFieldModel) => boolean): Observable<SelectItem[]> {
		return this.loadDynamicConfig().pipe(
			map((conf: DynamicConfigModel) => {
				let fields = conf.fieldList;

				if (filterFunc) {
					fields = fields.filter(filterFunc);
				}
				return fields.map((field: DynamicFieldModel) => {
					return {id: field.id, value: field.fieldName};
				});
			})
		);
	}

	// Returns all fields - not filtered by technical or sth. like that
	getRawDynamicFieldsAsSelectItem(): Observable<SelectItem[]> {
		return this.loadRawDynamicConfig().pipe(
			map((conf: DynamicConfigModel) => {
				return conf.fieldList.map((field: DynamicFieldModel) => {
					return {id: field.id, value: field.fieldName};
				});
			})
		);
	}

	getDynamicFieldNames(): Observable<string[]> {
		return this.loadDynamicConfig().pipe(
			map((conf: DynamicConfigModel) => {
				return conf.fieldList.map((entry: DynamicFieldModel) => {
					return entry.fieldName;
				});
			})
		);
	}

	getDynamicFreetextFields(): Observable<DynamicFieldBaseModel[]> {
		return this.loadDynamicConfig().pipe(
			map((conf: DynamicConfigModel) => {
				return conf.fieldList.filter((field: DynamicFieldModel) => {
					return field.fieldType === FieldTypes.freeText;
				});
			})
		);
	}

	getDynamicFreetextFieldsAsSelectItems(): Observable<SelectItem[]> {
		return this.getDynamicFreetextFields().pipe(
			map((fields: DynamicFieldBaseModel[]) => {
				return fields.map((field: DynamicFieldModel) => {
					return {id: field.id, value: field.fieldName};
				});
			})
		);
	}

	getDynamicFreeTextFieldsMappedByAssociatedLabel(): Observable<Map<string, SelectItem[]>> {
		return this.getDynamicFreetextFields().pipe(
			map((fields: DynamicFieldBaseModel[]) => {
				return this.mapDynamicFieldsToAssociatedLabelAsSelectItems(fields);
			})
		);
	}

	// Mapping-Ziele für den Importer-Mapping-Step: freeText- UND reference-
	// Felder (Sub-Labels), inklusive technischer Felder — die Order-Labels
	// (Bestellung/Bestellposition/Adresse) sind technisch und sonst unsichtbar.
	getMappableFieldsMappedByAssociatedLabel(): Observable<Map<string, SelectItem[]>> {
		return this.loadRawDynamicConfig().pipe(
			map((conf: DynamicConfigModel) => {
				return this.mapDynamicFieldsToAssociatedLabelAsSelectItems(this.filterMappableFields(conf.fieldList));
			})
		);
	}

	// Roh-Liste der mapping-fähigen Felder (freeText + reference/Sub-Labels,
	// inkl. technischer) als DynamicFieldModel[]. Pendant zu
	// getMappableFieldsMappedByAssociatedLabel, nur unaggregiert — der Exporter
	// braucht die Felder, um zu einem bereits gemappten Feld dessen Label
	// aufzulösen (sonst fallen technische Mappings durchs Raster).
	getMappableFields(): Observable<DynamicFieldModel[]> {
		return this.loadRawDynamicConfig().pipe(
			map((conf: DynamicConfigModel) => this.filterMappableFields(conf.fieldList))
		);
	}

	private filterMappableFields(fields: DynamicFieldModel[]): DynamicFieldModel[] {
		return fields.filter((field: DynamicFieldModel) => {
			return field.fieldType === FieldTypes.freeText || field.fieldType === FieldTypes.reference;
		});
	}

	getDisplayFields(): Observable<DynamicFieldBaseModel[]> {
		return this.loadDynamicConfig().pipe(
			map((conf: DynamicConfigModel) => {
				console.log(conf.fieldList);
				return conf.fieldList
					.filter(this.removeStockFieldsFilter)
					.filter(this.removeTechnicalFieldsFilter);
			})
		);
	}

	private removeStockFieldsFilter: (field: DynamicFieldModel) => boolean = (field: DynamicFieldModel) => {
		return field.label !== LabelUUIDs.Stock;
	}
	private removeTechnicalFieldsFilter: (field: DynamicFieldModel) => boolean = (field: DynamicFieldModel) => {
		return !field.isTechnical;
	}

	private mapDynamicFieldsToAssociatedLabelAsSelectItems(fields: DynamicFieldModel[]): Map<string, SelectItem[]> {
		const labelFieldMap = new Map<string, SelectItem[]>();
		fields.forEach((entry: DynamicFieldModel) => {
			const fieldSelectItem = {id: entry.id, value: entry.fieldName};
			if (!entry.label) {
				return;
			}
			const labelFieldList = labelFieldMap.get(entry.label);
			if (labelFieldList) {
				labelFieldList.push(fieldSelectItem);
			} else {
				labelFieldMap.set(entry.label, [fieldSelectItem]);
			}
		});
		return labelFieldMap;
	}

	loadDynamicConfig(): Observable<DynamicConfigModel> {
		if (!this.dynamicConfig$) {
			const completeConfig = this.getDynamicConfig();
			// Filtered version
			this.dynamicConfig$ = completeConfig.pipe(
				map((dynamicConfig: DynamicConfigModel) => {
					dynamicConfig.fieldList = dynamicConfig.fieldList?.filter((field: DynamicFieldModel) => {
						return !field.isTechnical && field.isVisible;
					}) || [];

					this.setFieldNamesMappedById(dynamicConfig);
					return dynamicConfig;
				})
			);
		}

		return this.dynamicConfig$;
	}

	loadRawDynamicConfig(): Observable<DynamicConfigModel> {
		if (!this.dynamicConfigRaw$) {
			this.dynamicConfigRaw$ = this.getDynamicConfig().pipe(
				map((dynamicConfig: DynamicConfigModel) => {
					// Workaround - will be removed once the lock-service provides the field on its own
					// synthetic: NUR Frontend-Anzeige — darf NIE ans Backend gespeichert
					// werden (das Product-Label existiert dort nicht zwingend → 500 beim
					// PUT /dynamic/config). Wird in modelToIdProperty rausgefiltert.
					dynamicConfig.fieldList.push({
						id: '6982884c-5eac-4220-907c-c93f397af273',
						fieldName: 'Artikelsperre',
						fieldType: FieldTypes.freeText,
						label: LabelUUIDs.Product,
						isMultiLingual: false,
						isTechnical: true,
						protected: true,
						isVisible: true,
						synthetic: true,
					} as DynamicFieldModel);
					return dynamicConfig;
				}),
				shareReplay(),
			);
		}

		return this.dynamicConfigRaw$;
	}

	updateDynamicConfig(configModel: DynamicConfigModel): Observable<DynamicConfigModel> {
		// expand=references auch beim PUT: die Response (= frische Config) kommt
		// dann mit aufgelösten referencedFields zurück, wie beim GET.
		const url = this.generateUrl(this.services.qdata, '%s', 'dynamic/config?expand=references');
		const updateModel = this.modelToIdProperty(configModel);

		return this.http.put<ObjectResponse<DynamicConfigModel>>(url, updateModel, this.options).pipe(
			map((res: ObjectResponse<DynamicConfigModel>) => {
					this.setFieldNamesMappedById(res.data);
					// Create new observable to force reload on all other sites after update
					this.dynamicConfig$ = this.getDynamicConfig();
					return res.data;
				}
			), catchError((error: HttpErrorResponse) => {
				// On Error create new to be sure the next use of dyn-config is the correct one
				// Done in service to not litter every component with this handling
				this.dynamicConfig$ = this.getDynamicConfig();
				return DynamicConfigService.handleError(error);
			})
		);
	}

	private setFieldNamesMappedById(data: DynamicConfigModel): void {
		this.fieldNameIdMap.clear();
		data.fieldList.forEach((entry: DynamicFieldModel) => {
			this.fieldNameIdMap.set(entry.fieldName.toLowerCase(), entry.id);
		});
	}

	private mapToSelectList(enumType: any, displayNameMap: Map<any, string>): SelectItem[] {
		return Object.keys(enumType).map((enumValue: any) => {
			return {id: enumValue, value: displayNameMap.get(enumValue) || ''};
		});
	}

	private getDynamicConfig(): Observable<DynamicConfigModel> {
		// expand=references: reference-Felder (Sub-Labels) tragen ihre Ziel-Label-
		// Felder serverseitig aufgelöst als referencedFields[]. Für Nicht-reference-
		// Felder ändert sich nichts. Siehe README_frontend_label_mapping.md.
		const url = this.generateUrl(this.services.qdata, '%s', 'dynamic/config?expand=references');

		return this.http.get<ObjectResponse<DynamicConfigModel>>(url, this.options).pipe(
			map((res: ObjectResponse<DynamicConfigModel>) => {
				return res.data;
			}),
			shareReplay(),
			catchError(DynamicConfigService.handleError)
		);
	}

	isFieldNameUniqueInConfig(value: string, id: string): any {
		const valueLowerCase = value?.toLowerCase() || value;
		if (!this.fieldNameIdMap.has(valueLowerCase)) {
			return true;
		}
		// On field-edit, ensure it is not the same field changed back to its original name
		return this.fieldNameIdMap.get(valueLowerCase) === id;
	}

	findReferencesFor(id: string, fieldList: DynamicFieldModel[]): string[] {
		if (!id) {
			return [];
		}

		return fieldList
			.filter(this.customFieldFilter)
			.filter(this.getComponentReferenceFilter(id))
			.map(this.fieldNameMapper);
	}

	private fieldNameMapper = (cField: DynamicFieldModel) => {
		return cField.fieldName;
	}

	private getComponentReferenceFilter(id: string): (f: DynamicFieldModel) => boolean {
		return (customField: DynamicFieldModel) => {
			return (customField as DynamicFieldCompositeModel).fieldComponents?.some((comp: CompositeFieldComponentPart) => {
				return comp.componentValue === id;
			});
		};
	}

	private customFieldFilter = (confField: DynamicFieldModel) => {
		return confField.fieldType === FieldTypes.custom;
	}

	private modelToIdProperty(configModel: DynamicConfigModel): DynamicConfigUpdateModel {
		//  maps the config to {"<field-uuid>": []<list of dynamic fields>}
		const model: DynamicConfigUpdateModel = {} as DynamicConfigUpdateModel;

		configModel.fieldList.forEach((entry: DynamicFieldModel) => {
			// Frontend-only-Workaround-Felder (z.B. Artikelsperre) NIE ans Backend
			// schicken — deren Label existiert dort nicht zwingend → 500 beim PUT.
			if ((entry as any).synthetic) {
				return;
			}
			// newly created field-components get a "new" property, to decide some workflows.
			// this property needs to removed, otherwise the BE returns the "new" property again
			// @ts-ignore
			delete entry.new;
			// referencedFields ist output-only (kommt nur via expand=references) —
			// nicht zurückschicken, das Backend ignoriert es ohnehin.
			delete entry.referencedFields;
			if (!model[entry.label]) {
				model[entry.label] = [];
			}
			model[entry.label].push(entry);
		});

		return model;
	}

	/**
	 * Return a function to filter out multilingual fields
	 */
	getMultilingualFilter(): (field: DynamicFieldModel) => boolean {
		return (field: DynamicFieldModel) => {
			return !field.isMultiLingual;
		};
	}
}
